import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { ReactNode, useCallback, useEffect, useRef } from 'react'

export const HorzScrollContainer = ({
  children,
  size = 0,
  width = 14,
  leftInset = 0,
}: {
  children: ReactNode
  size?: number
  width?: number
  /** Shifts the left scroller past columns that stay fixed while the rest of the table scrolls. */
  leftInset?: number | string
}) => {
  const refContent = useRef<HTMLDivElement>(null)
  const refScrollLeft = useRef<HTMLDivElement>(null)
  const refScrollRight = useRef<HTMLDivElement>(null)
  const scrollStepWidth = 250
  const overlayWidth = `${width * 0.25}rem`
  const overlayPadding = `${Math.max(0, (width - 6) / 2) * 0.25}rem`

  const onScrollListener = useCallback(() => {
    const tableContent = refContent.current
    const leftScroller = refScrollLeft.current
    const rightScroller = refScrollRight.current
    if (!tableContent || !leftScroller || !rightScroller) return

    const { clientWidth, scrollWidth, scrollLeft } = tableContent
    if (clientWidth >= scrollWidth) {
      leftScroller.style.display = 'none'
      rightScroller.style.display = 'none'
      return
    }

    leftScroller.style.display = scrollLeft <= 0 ? 'none' : 'flex'
    rightScroller.style.display = scrollLeft >= scrollWidth - clientWidth - 2 ? 'none' : 'flex'
  }, [])

  useEffect(() => {
    const tableContent = refContent.current
    if (!tableContent) return

    tableContent.addEventListener('scroll', onScrollListener)
    window.addEventListener('resize', onScrollListener)

    const resizeObserver = new ResizeObserver(onScrollListener)
    resizeObserver.observe(tableContent)

    return () => {
      tableContent.removeEventListener('scroll', onScrollListener)
      window.removeEventListener('resize', onScrollListener)
      resizeObserver.disconnect()
    }
  }, [onScrollListener])

  useEffect(() => {
    requestAnimationFrame(onScrollListener)
  }, [size, onScrollListener])

  const onScroll = (offset: number) => {
    refContent.current?.scrollBy({ left: offset, behavior: 'smooth' })
  }

  // These scroll affordances fade the edge of the content into the panel behind them,
  // so the gradient has to follow the panel. It used to ramp from a hardcoded white to
  // a fixed light grey, which painted a bright vertical band down the edge of every
  // scrollable grid in the dark styles. `surface` is the same token the panels use, so
  // the fade now tracks whichever palette is active.
  // (Deliberately no literal class names above: Tailwind scans comments too, and an
  // arbitrary-value token written here is emitted as a real, unused rule.)
  const overlayBaseClass =
    'absolute top-0 bottom-0 items-center justify-center cursor-pointer z-40 hidden hover:to-surface/80'

  return (
    <div className="relative max-w-full">
      <div
        className={`${overlayBaseClass} bg-gradient-to-r from-surface to-surface/50`}
        style={{ left: leftInset, width: overlayWidth, paddingLeft: overlayPadding, paddingRight: overlayPadding }}
        onClick={() => onScroll(-scrollStepWidth)}
        ref={refScrollLeft}
      >
        <ChevronLeftIcon className="w-6 h-6" />
      </div>
      <div
        className={`${overlayBaseClass} right-0 bg-gradient-to-l from-surface to-surface/50`}
        style={{ width: overlayWidth, paddingLeft: overlayPadding, paddingRight: overlayPadding }}
        onClick={() => onScroll(scrollStepWidth)}
        ref={refScrollRight}
      >
        <ChevronRightIcon className="w-6 h-6" />
      </div>

      <div className="overflow-x-auto overflow-y-hidden" ref={refContent}>
        {children}
      </div>
    </div>
  )
}
